'use client'

import { createContext, useContext, useMemo, type ReactNode, type AnchorHTMLAttributes } from 'react'
import { useRouter as useNextRouter, usePathname } from 'next/navigation'
import NextLink from 'next/link'
import {
  LOCALES,
  parse,
  href,
  type Locale,
  type L10n,
  type Route,
} from './locales'

export * from './locales'

const Ctx = createContext<{ route: Route; navigate: (to: string) => void }>({
  route: { locale: 'en', segments: [], path: '/' },
  navigate: () => {},
})

export function RouterProvider({
  children,
  locale: initialLocale,
}: {
  children: ReactNode
  locale?: Locale
}) {
  const pathname = usePathname() || '/'
  const nextRouter = useNextRouter()

  const route = useMemo(() => {
    const r = parse(pathname)
    if (initialLocale && LOCALES.includes(initialLocale)) {
      return { ...r, locale: initialLocale }
    }
    return r
  }, [pathname, initialLocale])

  const navigate = (to: string) => {
    const [pathPart, hash] = to.split('#')
    const target = pathPart || '/'
    if (hash) {
      nextRouter.push(`${target}#${hash}`)
    } else {
      nextRouter.push(target)
    }
  }

  return <Ctx.Provider value={{ route, navigate }}>{children}</Ctx.Provider>
}

export function useRouter() {
  return useContext(Ctx)
}

/** Translate a bilingual value for the current locale (Client component hook). */
export function useT() {
  const { route } = useRouter()
  return (v: L10n) => v[route.locale]
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; locale?: Locale }

/** Internal link. `to` is locale-neutral; external/mailto links pass through. */
export function Link({ to, locale, onClick, children, ...rest }: LinkProps) {
  const { route } = useRouter()
  const external = /^(https?:|mailto:|tel:)/.test(to)
  const target = external ? to : href(locale ?? route.locale, to)

  if (external) {
    return (
      <a
        href={target}
        onClick={onClick}
        {...(!to.startsWith('mailto:') && !to.startsWith('tel:') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...rest}
      >
        {children}
      </a>
    )
  }

  return (
    <NextLink href={target} onClick={onClick} {...rest}>
      {children}
    </NextLink>
  )
}
